import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  CopyButton,
  FullscreenButton,
  Panel,
  ResetButton,
  SoundToggle,
  usePersistentState,
  useSfx,
} from "@/components/tools/ToolKit";
import { Trophy, Plus, Minus, Trash2, RotateCcw, Printer, Crown } from "lucide-react";
import { toast } from "sonner";

export interface Team {
  id: string;
  name: string;
  score: number;
}

export function Scoreboard() {
  const [teams, setTeams] = usePersistentState<Team[]>("pickamini:scoreboard", [
    { id: "team-a", name: "Team Red", score: 0 },
    { id: "team-b", name: "Team Blue", score: 0 },
  ]);
  const [soundOn, setSoundOn] = usePersistentState("pickamini:scoreboard:sound", true);
  const [step, setStep] = useState(1);
  const stageRef = useRef<HTMLDivElement>(null);
  const sfx = useSfx(soundOn);

  const updateScore = (id: string, delta: number) => {
    sfx.click();
    setTeams((prev) =>
      prev.map((t) => (t.id === id ? { ...t, score: Math.max(0, t.score + delta) } : t)),
    );
  };

  const addTeam = () => {
    sfx.click();
    if (teams.length >= 12) {
      toast.error("Maximum 12 teams allowed");
      return;
    }
    const teamLetter = String.fromCharCode(65 + teams.length);
    setTeams((prev) => [
      ...prev,
      {
        id: `team-${Date.now()}`,
        name: `Team ${teamLetter}`,
        score: 0,
      },
    ]);
  };

  const removeTeam = (id: string) => {
    if (teams.length <= 1) {
      toast.error("Keep at least 1 team");
      return;
    }
    sfx.click();
    setTeams((prev) => prev.filter((t) => t.id !== id));
  };

  const resetAllScores = () => {
    sfx.click();
    setTeams((prev) => prev.map((t) => ({ ...t, score: 0 })));
    toast.success("Reset all scores to 0");
  };

  const maxScore = Math.max(0, ...teams.map((t) => t.score));
  const leaders = teams.filter((t) => t.score === maxScore && maxScore > 0);

  const asText = teams
    .map((t, idx) => `${idx + 1}. ${t.name}: ${t.score} Points`)
    .join("\n");

  return (
    <div className="grid gap-5 lg:grid-cols-[300px_1fr]">
      {/* Settings & Setup Panel */}
      <Panel title="Scoreboard Options" className="no-print space-y-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-1.5">
            Points Increment Step
          </label>
          <div className="flex gap-1.5">
            {[1, 5, 10].map((s) => (
              <Button
                key={s}
                type="button"
                size="sm"
                variant={step === s ? "default" : "outline"}
                onClick={() => setStep(s)}
                className="flex-1 font-bold"
              >
                +{s} / -{s}
              </Button>
            ))}
          </div>
        </div>

        <div className="pt-2 space-y-2">
          <Button onClick={addTeam} variant="outline" className="w-full font-semibold">
            <Plus className="size-4 mr-1.5" /> Add Team
          </Button>

          <Button onClick={resetAllScores} variant="outline" className="w-full font-semibold text-xs">
            <RotateCcw className="size-3.5 mr-1.5" /> Reset All Scores
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 pt-2 border-t">
          <SoundToggle on={soundOn} onToggle={() => setSoundOn(!soundOn)} />
          <CopyButton text={asText} label="Copy Scores" />
        </div>
      </Panel>

      {/* Main Scoreboard Stage Container */}
      <div
        ref={stageRef}
        className="surface-card flex flex-col justify-between p-5 sm:p-6 rounded-2xl bg-card border shadow-soft space-y-5"
      >
        {/* Header Control Bar */}
        <div className="no-print flex flex-wrap items-center justify-between gap-3 border-b pb-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-sm font-bold text-foreground">
              <Trophy className="size-4 text-amber-500" />
              <span>{teams.length} Teams</span>
            </div>

            {leaders.length > 0 && (
              <div className="flex items-center gap-1.5 bg-amber-500/10 border border-amber-500/30 px-3 py-1 rounded-full text-xs font-bold text-amber-600 dark:text-amber-400">
                <Crown className="size-3.5 text-amber-500" />
                <span>Leader: {leaders.map((l) => l.name).join(", ")}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-1">
              {[1, 5, 10].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStep(s)}
                  className={`px-2.5 py-1 rounded-md text-xs font-bold transition-colors ${
                    step === s
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-muted-foreground hover:text-foreground"
                  }`}
                >
                  +{s}
                </button>
              ))}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={addTeam}
              className="text-xs font-semibold"
            >
              <Plus className="size-3.5 mr-1" /> Team
            </Button>

            <Button variant="outline" size="sm" onClick={() => window.print()}>
              <Printer className="size-3.5 mr-1" /> Print
            </Button>

            <FullscreenButton targetRef={stageRef} />
          </div>
        </div>

        {/* PRINT ONLY HEADER */}
        <div className="hidden print:block mb-6 text-center border-b pb-4">
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">
            Classroom Scoreboard Summary
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Date: {new Date().toLocaleDateString()} • {teams.length} Teams
          </p>
        </div>

        {/* Score Cards Grid (3-4 columns max so cards have plenty of space) */}
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 print:grid-cols-2 my-auto">
          {teams.map((team) => {
            const isLeader = team.score === maxScore && maxScore > 0;

            return (
              <div
                key={team.id}
                className={`relative flex flex-col justify-between rounded-2xl border p-4 sm:p-5 transition-all shadow-sm bg-card overflow-hidden ${
                  isLeader
                    ? "border-amber-400 ring-2 ring-amber-400/40 bg-amber-500/5 dark:bg-amber-500/10"
                    : "border-border hover:border-primary/40"
                }`}
              >
                {/* Header inside card: Name + Leader Badge + Delete */}
                <div>
                  <div className="flex items-center justify-between gap-2 border-b pb-2 mb-3">
                    <Input
                      value={team.name}
                      onChange={(e) =>
                        setTeams((prev) =>
                          prev.map((t) => (t.id === team.id ? { ...t, name: e.target.value } : t)),
                        )
                      }
                      className="border-none bg-transparent p-0 font-display text-base sm:text-lg font-bold text-foreground focus-visible:ring-0 focus-visible:ring-offset-0 truncate"
                    />

                    <div className="flex items-center gap-1 shrink-0">
                      {isLeader && (
                        <span className="flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-extrabold text-slate-950 shadow-sm">
                          <Crown className="size-3 stroke-[2.5]" /> LEADER
                        </span>
                      )}

                      {teams.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeTeam(team.id)}
                          className="no-print text-muted-foreground hover:text-red-500 transition-colors p-1"
                          title="Remove team"
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Big Number Score */}
                  <div className="my-3 text-center">
                    <span
                      key={team.score}
                      className={`font-display text-5xl sm:text-6xl font-extrabold tracking-tight ${
                        isLeader ? "text-amber-500" : "text-primary"
                      }`}
                    >
                      {team.score}
                    </span>
                  </div>
                </div>

                {/* Score Adjust Control Buttons (100% contained inside card) */}
                <div className="no-print grid grid-cols-2 gap-2 pt-2 mt-auto">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => updateScore(team.id, -step)}
                    className="w-full font-bold h-10 text-sm"
                    disabled={team.score === 0}
                  >
                    <Minus className="size-3.5 mr-1" /> {step}
                  </Button>

                  <Button
                    type="button"
                    size="sm"
                    onClick={() => updateScore(team.id, step)}
                    className="w-full font-bold h-10 text-sm"
                  >
                    <Plus className="size-3.5 mr-1" /> {step}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
